<div class="modal-overlay">
  <div class="modal-container">

    <!-- Header -->
    <div class="modal-header">
      <h2>{{ isEditMode ? 'Edit Meeting' : 'Schedule New Meeting' }}</h2>
      <mat-icon class="close-icon" (click)="closeModal()">close</mat-icon>
    </div>

    <mat-dialog-content>
      <div class="modal-body">

        <form class="form-body" [formGroup]="meetingForm">

          <!-- Subject -->
          <div class="field-group">
            <label class="field-label">Subject <span class="required">*</span></label>
            <input type="text" class="form-input" formControlName="meetingSubject" placeholder="Meeting Subject" />
            <span class="field-error"
                  *ngIf="formSubmitted && meetingForm.get('meetingSubject')?.hasError('required')">
              Subject is required
            </span>
          </div>

          <!-- Attendees -->
          <div class="field-group">
            <label class="field-label">Attendees <span class="required">*</span></label>
            <user-autocomplete
              formControlName="attendeesIds"
              [label]="''"
              [placeholder]="'Search by name or email to add attendees…'"
              [searchFn]="searchUserDataList"></user-autocomplete>
            <span class="field-error"
                  *ngIf="formSubmitted && meetingForm.get('attendeesIds')?.hasError('required')">
              At least one attendee is required
            </span>
          </div>

          <!-- Hospital + Quarter -->
          <div class="two-col">
            <div class="field-group">
              <label class="field-label">Hospital <span class="required">*</span></label>
              <div class="autocomplete-wrap">
                <input type="text"
                       class="form-input"
                       [formControl]="meetingForm.get('hospital')"
                       [matAutocomplete]="autoHospital"
                       placeholder="Choose a hospital…" />
                <button *ngIf="meetingForm.get('hospital')?.value"
                        type="button"
                        class="clear-hospital"
                        aria-label="Clear hospital"
                        (mousedown)="$event.preventDefault()"
                        (click)="clearHospital($event)">
                  <mat-icon>close</mat-icon>
                </button>
                <mat-autocomplete #autoHospital="matAutocomplete" autoActiveFirstOption [displayWith]="displayHospitalName">
                  <mat-option *ngFor="let h of filteredHospitalList" [value]="h">
                    {{ h.practiceName }}
                  </mat-option>
                </mat-autocomplete>
              </div>
              <span class="field-error"
                    *ngIf="formSubmitted && meetingForm.get('hospital')?.hasError('required')">
                Hospital is required
              </span>
            </div>

            <div class="field-group">
              <label class="field-label">Quarter (for Goal selection)</label>
              <div class="select-wrap">
                <mat-select class="form-select" formControlName="quarter" placeholder="Choose quarter…">
                  <mat-option *ngFor="let q of quarterList" [value]="q">{{ q.quarterName }}</mat-option>
                </mat-select>
                <span class="material-icons arrow">expand_more</span>
              </div>
            </div>
          </div>

          <!-- Goal -->
          <div class="field-group"
               *ngIf="meetingForm.get('hospital')?.value && meetingForm.get('quarter')?.value && goalList.length > 0">
            <label class="field-label">Goal</label>
            <div class="select-wrap">
              <mat-select class="form-select" formControlName="affiliatedGoal" placeholder="Choose a goal…">
                <mat-option *ngFor="let goal of goalList" [value]="goal.goalTargetId">{{ goal.goalName }}</mat-option>
              </mat-select>
              <span class="material-icons arrow">expand_more</span>
            </div>
          </div>

          <!-- Meeting Type -->
          <div class="field-group">
            <label class="field-label">Meeting Type <span class="required">*</span></label>
            <div class="select-wrap">
              <mat-select class="form-select" formControlName="meetingType" placeholder="Choose a meeting type…">
                <mat-option *ngFor="let mt of meetingTypeOptions" [value]="mt.value">{{ mt.label }}</mat-option>
              </mat-select>
              <span class="material-icons arrow">expand_more</span>
            </div>
            <span class="field-error"
                  *ngIf="formSubmitted && meetingForm.get('meetingType')?.hasError('required')">
              Meeting type is required
            </span>
          </div>

          <!-- Scheduled Date + Time Zone -->
          <div class="two-col">
            <div class="field-group">
              <label class="field-label">Scheduled Date <span class="required">*</span></label>
              <div class="date-wrap">
                <input class="form-input" [matDatepicker]="datePicker"
                       formControlName="scheduledDate" [min]="minDate"
                       placeholder="MM/DD/YYYY" readonly />
                <mat-datepicker-toggle matIconSuffix [for]="datePicker" class="date-toggle"></mat-datepicker-toggle>
                <mat-datepicker #datePicker></mat-datepicker>
              </div>
              <span class="field-error"
                    *ngIf="formSubmitted && meetingForm.get('scheduledDate')?.hasError('required')">
                Scheduled date is required
              </span>
            </div>

            <div class="field-group">
              <label class="field-label">Time Zone <span class="required">*</span></label>
              <div class="select-wrap">
                <mat-select class="form-select" formControlName="timezone" placeholder="Choose timezone…">
                  <mat-option *ngFor="let tz of timeZoneOptions" [value]="tz.value">{{ tz.label }}</mat-option>
                </mat-select>
                <span class="material-icons arrow">expand_more</span>
              </div>
              <span class="field-error"
                    *ngIf="formSubmitted && meetingForm.get('timezone')?.hasError('required')">
                Time zone is required
              </span>
            </div>
          </div>

          <!-- Start Time + End Time -->
          <div class="two-col">
            <div class="field-group">
              <label class="field-label">Start Time <span class="required">*</span></label>
              <div class="time-wrap">
                <input class="form-input" [matTimepicker]="startPicker"
                       formControlName="startTime" placeholder="--:-- --" />
                <mat-timepicker-toggle matIconSuffix [for]="startPicker" class="time-toggle"></mat-timepicker-toggle>
                <mat-timepicker #startPicker></mat-timepicker>
              </div>
              <span class="field-error"
                    *ngIf="formSubmitted && meetingForm.get('startTime')?.hasError('required')">
                Start time is required
              </span>
              <span class="field-error"
                    *ngIf="meetingForm.get('startTime')?.hasError('timePassed') && meetingForm.get('startTime')?.touched">
                Start time cannot be in the past
              </span>
            </div>

            <div class="field-group">
              <label class="field-label">End Time <span class="required">*</span></label>
              <div class="time-wrap">
                <input class="form-input" [matTimepicker]="endPicker"
                       formControlName="endTime" placeholder="--:-- --" />
                <mat-timepicker-toggle matIconSuffix [for]="endPicker" class="time-toggle"></mat-timepicker-toggle>
                <mat-timepicker #endPicker></mat-timepicker>
              </div>
              <span class="field-error"
                    *ngIf="formSubmitted && meetingForm.get('endTime')?.hasError('required')">
                End time is required
              </span>
              <span class="field-error"
                    *ngIf="meetingForm.hasError('endBeforeStart') && meetingForm.get('endTime')?.touched">
                End time must be after start time
              </span>
            </div>
          </div>

          <!-- Agenda -->
          <div class="field-group">
            <label class="field-label">Agenda</label>
            <textarea class="form-input form-textarea"
                      formControlName="agenda"
                      placeholder="Optional — context for attendees. Appears in the Teams meeting invite."
                      rows="3"></textarea>
          </div>

          <!-- Recurrence Toggle -->
          <div class="field-group">
            <label class="field-label">Recurring Meeting</label>
            <mat-slide-toggle formControlName="isRecurring" color="primary"
                              [disabled]="isEditMode && editMeetingData?.isRecurring">
              {{ meetingForm.get('isRecurring')?.value ? 'Yes — this meeting will repeat' : 'No — one-time meeting' }}
            </mat-slide-toggle>
          </div>

          <!-- Edit Mode: Recurrence Read-Only Notice -->
          <div *ngIf="isEditMode && editMeetingData?.isRecurring" class="recurrence-section recurrence-readonly">
            <div class="recurrence-readonly-header">
              <span class="material-icons recurrence-readonly-icon">lock</span>
              <span class="recurrence-readonly-title">Recurrence Settings (Read-Only)</span>
            </div>
            <div class="recurrence-readonly-desc">{{ getRecurrenceDescription() }}</div>
            <div class="recurrence-readonly-note">
              <span class="material-icons" style="font-size: 14px; color: #94a3b8;">info</span>
              Recurrence settings cannot be changed after meeting creation. To change recurrence, delete this meeting and create a new one.
            </div>
          </div>

          <!-- Granular Recurrence Controls (Teams/Outlook style) — Create mode only -->
          <div *ngIf="meetingForm.get('isRecurring')?.value && !isEditMode" class="recurrence-section">

            <!-- Repeat every N <frequency> -->
            <div class="recurrence-row">
              <span class="recurrence-label">Repeat every</span>
              <input type="number" class="recurrence-interval-input"
                     formControlName="recurrenceInterval" min="1" max="99" />
              <div class="select-wrap recurrence-freq-select">
                <mat-select class="form-select" formControlName="recurrenceFrequency" placeholder="frequency">
                  <mat-option *ngFor="let f of frequencyOptions" [value]="f.value">{{ f.label }}</mat-option>
                </mat-select>
                <span class="material-icons arrow">expand_more</span>
              </div>
            </div>

            <!-- Day of week selector (only for weekly) -->
            <div *ngIf="isWeeklyFrequency" class="recurrence-row dow-row">
              <span class="recurrence-label">On</span>
              <div class="dow-buttons">
                <button *ngFor="let day of dayOfWeekOptions; let i = index"
                        type="button"
                        class="dow-btn"
                        [class.selected]="isDaySelected(day.value)"
                        (click)="toggleDayOfWeek(day.value)">
                  {{ day.label }}
                </button>
              </div>
            </div>

            <!-- Until date -->
            <div class="recurrence-row">
              <span class="recurrence-label">Until</span>
              <div class="date-wrap recurrence-date-wrap">
                <input class="form-input" [matDatepicker]="recurrenceEndDatePicker"
                       formControlName="recurrenceEndDate" [min]="minDate"
                       placeholder="MM/DD/YYYY" readonly />
                <mat-datepicker-toggle matIconSuffix [for]="recurrenceEndDatePicker" class="date-toggle"></mat-datepicker-toggle>
                <mat-datepicker #recurrenceEndDatePicker></mat-datepicker>
              </div>
            </div>

            <span class="field-error"
                  *ngIf="formSubmitted && !meetingForm.get('recurrenceFrequency')?.value">
              Frequency is required for recurring meetings
            </span>
            <span class="field-error"
                  *ngIf="formSubmitted && !meetingForm.get('recurrenceEndDate')?.value">
              End date is required for recurring meetings
            </span>

            <!-- Recurrence description -->
            <div class="recurrence-description" *ngIf="getRecurrenceDescription()">
              <mat-icon class="recurrence-icon">event_repeat</mat-icon>
              {{ getRecurrenceDescription() }}
            </div>

            <!-- Upcoming Occurrences Preview -->
            <div class="field-group" *ngIf="upcomingOccurrences.length > 0">
              <div class="occurrences-header">
                <label class="field-label">Upcoming Occurrences ({{ upcomingOccurrences.length }})</label>
                <button type="button" class="view-schedule-btn" (click)="showOccurrences = !showOccurrences">
                  <mat-icon>{{ showOccurrences ? 'visibility_off' : 'visibility' }}</mat-icon>
                  {{ showOccurrences ? 'Hide' : 'View' }} Schedule
                </button>
              </div>
              <div class="occurrences-table-wrap" *ngIf="showOccurrences">
                <table class="occurrences-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Occurrence Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *ngFor="let occ of upcomingOccurrences; let i = index">
                      <td>{{ i + 1 }}</td>
                      <td>{{ occ.display }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

        </form>
      </div>
    </mat-dialog-content>

    <mat-dialog-actions>
      <div class="modal-footer">
        <button type="button" class="cancel-btn" (click)="closeModal()">Cancel</button>
        <button type="button" class="v-button v-button-md v-green" [disabled]="loading" (click)="onSave()">
          <ng-container *ngIf="!loading">{{ isEditMode ? 'Update Meeting' : 'Save Meeting' }}</ng-container>
          <ng-container *ngIf="loading">
            <i class="pi pi-spin pi-spinner mr-2"></i>
            {{ isEditMode ? 'Updating...' : 'Saving...' }}
          </ng-container>
        </button>
      </div>
    </mat-dialog-actions>

  </div>
</div>
